/**
 * seatUtils.js — Dimensions réelles + calcul sièges
 * Tout est en PIXELS mais basé sur une échelle réelle en mètres.
 *
 * Échelle par défaut : 60px = 1m (configurable dans le plan)
 */

// ───────────────────────────────────────────────────────
// CONSTANTES D'ÉCHELLE
// ───────────────────────────────────────────────────────
export const DEFAULT_PX_PER_M = 60;   // 60 pixels = 1 mètre
export const DEFAULT_ROOM_W_M = 15;   // Salle 15m de large par défaut
export const DEFAULT_ROOM_H_M = 10;   // Salle 10m de profondeur par défaut
export const ROOM_PADDING_M   = 1.5;  // Espace autour de la salle (1.5m)

// Convertit des mètres en pixels selon l'échelle
export const mToPx = (meters, pxPerM = DEFAULT_PX_PER_M) => meters * pxPerM;
export const pxToM = (px,    pxPerM = DEFAULT_PX_PER_M) => px / pxPerM;

// ───────────────────────────────────────────────────────
// DIMENSIONS RÉELLES DES TABLES (en mètres)
// Sources : normes mobilier événementiel français
// ───────────────────────────────────────────────────────
export const TABLE_DIMS_M = {
  // Tables avec sièges
  round:    { wm: 1.50, hm: 1.50, defaultCap: 8,  label: 'Ronde Ø1.5m'     },
  oval:     { wm: 1.80, hm: 1.20, defaultCap: 8,  label: 'Ovale 1.8m'      },
  rect:     { wm: 1.80, hm: 0.75, defaultCap: 8,  label: 'Rectangulaire'   },
  square:   { wm: 0.90, hm: 0.90, defaultCap: 4,  label: 'Carrée 90cm'     },
  honor:    { wm: 4.00, hm: 0.75, defaultCap: 12, label: 'Table d\'honneur' },
  cocktail: { wm: 0.60, hm: 0.60, defaultCap: 4,  label: 'Cocktail Ø60cm'  },
  // Éléments de décor
  dj:       { wm: 1.50, hm: 1.00, defaultCap: 0, label: 'DJ'            },
  dance:    { wm: 4.00, hm: 3.00, defaultCap: 0, label: 'Piste de danse'},
  stage:    { wm: 5.00, hm: 2.00, defaultCap: 0, label: 'Scène'         },
  buffet:   { wm: 3.00, hm: 0.75, defaultCap: 0, label: 'Buffet'        },
  bar:      { wm: 2.00, hm: 0.75, defaultCap: 0, label: 'Bar'           },
  cake:     { wm: 0.80, hm: 0.80, defaultCap: 0, label: 'Pièce montée' },
  photo:    { wm: 1.50, hm: 1.20, defaultCap: 0, label: 'Photobooth'   },
  welcome:  { wm: 1.20, hm: 0.80, defaultCap: 0, label: 'Accueil'      },
  kids:     { wm: 2.00, hm: 1.20, defaultCap: 0, label: 'Espace enfants'},
  door:     { wm: 1.00, hm: 0.30, defaultCap: 0, label: 'Entrée'       },
};

/** Retourne w/h en pixels pour un type de table selon l'échelle courante */
export function getTablePxDims(type, pxPerM = DEFAULT_PX_PER_M) {
  const dims = TABLE_DIMS_M[type];
  if (!dims) return { w: pxPerM, h: pxPerM };
  return {
    w: Math.round(dims.wm * pxPerM),
    h: Math.round(dims.hm * pxPerM),
  };
}

/** Formate une distance en pixels en texte lisible (m / cm) */
export function formatDistance(px, pxPerM = DEFAULT_PX_PER_M) {
  const meters = px / pxPerM;
  if (meters >= 1) return `${meters.toFixed(1)}m`;
  return `${Math.round(meters * 100)}cm`;
}

// ───────────────────────────────────────────────────────
// CALCUL DES SIÈGES
// ───────────────────────────────────────────────────────
export const SEAT_SIZE = 18;
const PAD = 18; // distance bord élément → centre siège

export function computeSeatPositions(type, w, h, capacity) {
  if (!capacity || capacity <= 0) return [];
  const s = SEAT_SIZE;
  const positions = [];

  if (type === 'round' || type === 'oval') {
    const cx = w / 2, cy = h / 2;
    const rx = w / 2 + PAD, ry = h / 2 + PAD;
    for (let i = 0; i < capacity; i++) {
      const angle = (2 * Math.PI * i) / capacity - Math.PI / 2;
      positions.push({ idx: i,
        x: cx + rx * Math.cos(angle) - s / 2,
        y: cy + ry * Math.sin(angle) - s / 2 });
    }
  } else if (type === 'rect' || type === 'honor' || type === 'square') {
    const top    = Math.ceil(capacity / 2);
    const bottom = capacity - top;
    for (let i = 0; i < top; i++)
      positions.push({ idx: i, x: ((i + 0.5) / top) * w - s / 2, y: -(PAD + s / 2) });
    for (let i = 0; i < bottom; i++)
      positions.push({ idx: top + i, x: ((i + 0.5) / bottom) * w - s / 2, y: h + PAD - s / 2 });
  } else if (type === 'cocktail') {
    const capped = Math.min(capacity, 4);
    const cx = w / 2, cy = h / 2;
    const r  = w / 2 + PAD;
    for (let i = 0; i < capped; i++) {
      const angle = (2 * Math.PI * i) / capped - Math.PI / 2;
      positions.push({ idx: i, x: cx + r * Math.cos(angle) - s/2, y: cy + r * Math.sin(angle) - s/2 });
    }
  }
  return positions;
}

export const SITABLE_TYPES = new Set(['round','oval','rect','honor','square','cocktail']);

export const TYPE_META = {
  round:    { label: 'Ronde',          icon: null },
  oval:     { label: 'Ovale',          icon: null },
  rect:     { label: 'Rectangulaire',  icon: null },
  square:   { label: 'Carrée',         icon: null },
  honor:    { label: 'Table d\'honneur', icon: null },
  cocktail: { label: 'Cocktail',       icon: null },
  dj:       { label: 'DJ',             decor: true },
  dance:    { label: 'Piste de danse', decor: true },
  stage:    { label: 'Scène',          decor: true },
  buffet:   { label: 'Buffet',         decor: true },
  bar:      { label: 'Bar',            decor: true },
  cake:     { label: 'Pièce montée',   decor: true },
  photo:    { label: 'Photobooth',     decor: true },
  welcome:  { label: 'Accueil',        decor: true },
  kids:     { label: 'Espace enfants', decor: true },
  door:     { label: 'Entrée',         decor: true },
};
